// Library methods
import React, { useState, useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useAuth0 } from "@auth0/auth0-react";

// MUI Components
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import Slider from "@mui/material/Slider";

// Utilities
import {
  deviceCalibrateLobbyRequested,
  deviceDisableVisualFIeldControllerBeepRequest,
  deviceEnableVisualFIeldControllerBeepRequest,
  deviceEnableVisualFieldWarningsRequest,
  deviceDisableVisualFieldWarningsRequest,
  deviceEnableSubtitlesRequest,
  deviceDisableSubtitlesRequest,
  deviceUpdateVolumeRequest,
} from "../../../../services/SignalR";

// Contexts
import { useLiveUpdateStatusContext } from "../../../../contexts/LiveUpdateStatusContext";

// Icons
import {
  CrosshairIcon,
  HeadphonesIcon,
  HeadphonesOffIcon,
  MaximizeIcon,
  MinimizeIcon,
  MusicIcon,
  MusicOffIcon,
  SpeechBubbleIcon,
  SpeechBubbleOffIcon,
  VolumeIcon,
} from "../../../../assets/icons/";

export const LiveUpdateActions = ({ connection }) => {
  const { t } = useTranslation();
  const { getAccessTokenSilently } = useAuth0();
  const {
    open,
    device,
    isOnline,
    isNotStarted,
    fullScreenModal,
    isVisualField,
    isEsterman,
    actions,
  } = useLiveUpdateStatusContext();
  const { setFullScreenModal } = actions;

  const [deviceStatusVolume, setDeviceStatusVolume] = useState(
    device?.deviceStatus?.volume
  );

  // Get the current volume on the device
  useEffect(() => {
    connection?.off("DeviceStatusUpdated");
    connection?.on("DeviceStatusUpdated", function (data) {
      const parsedData = JSON.parse(data);
      setDeviceStatusVolume(parsedData?.data?.deviceStatus?.volume);
    });
  }, [connection]);

  const intraExamProperties = device?.deviceStatus?.intraExamProperties;

  const defaultVolume =
    open && deviceStatusVolume && !isNaN(deviceStatusVolume)
      ? Math.round(deviceStatusVolume * 100)
      : 0;

  const [volume, setVolume] = useState(defaultVolume);
  const [mouseDownVolume, setMouseDownVolume] = useState(false);
  const [mouseUpVolume, setMouseUpVolume] = useState(false);

  const [shouldUpdateButtons, setShouldUpdateButtons] = useState(false);
  const [isBeepEnabled, setIsBeepEnabled] = useState(
    intraExamProperties?.visualFieldControllerBeepsEnabled
  );
  const [isWarningsEnabled, setIsWarningsEnabled] = useState(
    intraExamProperties?.visualFieldWarningsEnabled
  );
  const [isSubtitlesEnabled, setIsSubtitlesEnabled] = useState(
    intraExamProperties?.subtitlesEnabled
  );

  useEffect(() => {
    if (!isOnline) {
      setShouldUpdateButtons(true);
    } else if (isOnline && shouldUpdateButtons) {
      setIsBeepEnabled(intraExamProperties?.visualFieldControllerBeepsEnabled);
      setIsWarningsEnabled(intraExamProperties?.visualFieldWarningsEnabled);
      setIsSubtitlesEnabled(intraExamProperties?.subtitlesEnabled);
      setShouldUpdateButtons(false);
    }
  }, [isOnline, intraExamProperties, shouldUpdateButtons]);

  const handleUpdateVolumeRequest = useCallback(
    async (volume) => {
      try {
        const token = await getAccessTokenSilently();
        await deviceUpdateVolumeRequest(token, device?.id, volume, connection);
        setMouseDownVolume(false);
        setMouseUpVolume(true);
      } catch (e) {
        console.error(e);
      }
    },
    [connection, device?.id, getAccessTokenSilently]
  );

  const handleVolumeChange = useCallback((event) => {
    setMouseDownVolume(true);
    const sliderValue = event.target.value;
    setVolume(sliderValue);
  }, []);

  const handleVolumeSet = useCallback(() => {
    const formattedVolume = parseFloat(volume / 100).toFixed(2);
    handleUpdateVolumeRequest(formattedVolume);
  }, [volume, handleUpdateVolumeRequest]);

  // Guard the volume on the modal when it is changed by the user
  useEffect(() => {
    const formattedVolume = Math.round(deviceStatusVolume * 100);

    if (formattedVolume === volume && !mouseDownVolume && mouseUpVolume) {
      setMouseUpVolume(false);
    }
  }, [mouseDownVolume, mouseUpVolume, deviceStatusVolume, volume]);

  // Update the volume on the modal if it is changed from the device status
  useEffect(() => {
    const formattedVolume = Math.round(deviceStatusVolume * 100);

    if (formattedVolume !== volume && !mouseDownVolume && !mouseUpVolume) {
      setVolume(formattedVolume);
    }
  }, [deviceStatusVolume, volume, mouseDownVolume, mouseUpVolume]);

  // Recenter patient view request
  const handleDeviceCalibrateLobbyRequest = useCallback(async () => {
    try {
      const token = await getAccessTokenSilently();
      await deviceCalibrateLobbyRequested(token, device?.id, connection);
    } catch (e) {
      console.error(e);
    }
  }, [connection, device?.id, getAccessTokenSilently]);

  // Enable controller beep sound
  const handleEnableVisualFieldControllerBeepRequest = useCallback(async () => {
    try {
      const token = await getAccessTokenSilently();
      await deviceEnableVisualFIeldControllerBeepRequest(
        token,
        device?.id,
        connection
      );
      setIsBeepEnabled(true);
    } catch (e) {
      console.error(e);
    }
  }, [connection, device?.id, getAccessTokenSilently]);

  // Disable controller beep sound
  const handleDisableVisualFieldControllerBeepRequest =
    useCallback(async () => {
      try {
        const token = await getAccessTokenSilently();
        await deviceDisableVisualFIeldControllerBeepRequest(
          token,
          device?.id,
          connection
        );
        setIsBeepEnabled(false);
      } catch (e) {
        console.error(e);
      }
    }, [connection, device?.id, getAccessTokenSilently]);

  // Enable warnings
  const handleEnableVisualFieldWarningsRequest = useCallback(async () => {
    try {
      const token = await getAccessTokenSilently();
      await deviceEnableVisualFieldWarningsRequest(
        token,
        device?.id,
        connection
      );
      setIsWarningsEnabled(true);
    } catch (e) {
      console.error(e);
    }
  }, [connection, device?.id, getAccessTokenSilently]);

  // Disable warnings
  const handleDisableVisualFieldWarningsRequest = useCallback(async () => {
    try {
      const token = await getAccessTokenSilently();
      await deviceDisableVisualFieldWarningsRequest(
        token,
        device?.id,
        connection
      );
      setIsWarningsEnabled(false);
    } catch (e) {
      console.error(e);
    }
  }, [connection, device?.id, getAccessTokenSilently]);

  // Enable subtitles
  const handleEnableSubtitlesRequest = useCallback(async () => {
    try {
      const token = await getAccessTokenSilently();
      await deviceEnableSubtitlesRequest(token, device?.id, connection);
      setIsSubtitlesEnabled(true);
    } catch (e) {
      console.error(e);
    }
  }, [connection, device?.id, getAccessTokenSilently]);

  // Disable subtitles
  const handleDisableSubtitlesRequest = useCallback(async () => {
    try {
      const token = await getAccessTokenSilently();
      await deviceDisableSubtitlesRequest(token, device?.id, connection);
      setIsSubtitlesEnabled(false);
    } catch (e) {
      console.error(e);
    }
  }, [connection, device?.id, getAccessTokenSilently]);

  return (
    <Box
      display="flex"
      flexDirection={{ xs: "column", sm: "row" }}
      alignItems={{ xs: "flex-start", sm: "center" }}
      justifyContent="space-between"
      gap="8px"
    >
      <Box
        display="flex"
        justifyContent="flex-end"
        alignItems="center"
        gap="4px"
      >
        <Box
          backgroundColor={isOnline ? "#22BE39" : "#435B6D7A"}
          borderRadius="100%"
          height="10px"
          width="10px"
        />
        <Typography fontSize="16px">
          {isOnline ? t("word_online") : t("word_offline")}
        </Typography>
        <Tooltip
          title={
            fullScreenModal
              ? t("word_minimize_modal")
              : t("word_maximize_modal")
          }
        >
          <span>
            <IconButton onClick={() => setFullScreenModal((prev) => !prev)}>
              {fullScreenModal ? (
                <MinimizeIcon color="#000" alt="minimize modal" />
              ) : (
                <MaximizeIcon color="#000" alt="maximize modal" />
              )}
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title={t("word_recenter_patient_view")}>
          <span>
            <IconButton
              onClick={handleDeviceCalibrateLobbyRequest}
              disabled={isNotStarted || !isOnline}
              sx={{
                opacity: isNotStarted || !isOnline ? 0.3 : 1,
              }}
            >
              <CrosshairIcon color="#000" alt="calibration" />
            </IconButton>
          </span>
        </Tooltip>
        {(isVisualField || isEsterman) && (
          <Tooltip
            title={
              isBeepEnabled
                ? t("word_disable_controller_beep")
                : t("word_enable_controller_beep")
            }
          >
            <span>
              <IconButton
                aria-label="controller-with-sound-effect"
                disabled={!isOnline}
                sx={{
                  opacity: !isOnline ? 0.3 : 1,
                }}
                onClick={
                  isBeepEnabled
                    ? handleDisableVisualFieldControllerBeepRequest
                    : handleEnableVisualFieldControllerBeepRequest
                }
              >
                {isBeepEnabled ? (
                  <MusicIcon color="#000" alt="controller-with-sound-effect" />
                ) : (
                  <MusicOffIcon
                    color="#000"
                    alt="controller-with-sound-effect"
                  />
                )}
              </IconButton>
            </span>
          </Tooltip>
        )}
        {(isVisualField || isEsterman) && (
          <Tooltip
            title={
              isWarningsEnabled
                ? t("word_disable_warnings")
                : t("word_enable_warnings")
            }
          >
            <span>
              <IconButton
                aria-label="headset-warnings"
                onClick={
                  isWarningsEnabled
                    ? handleDisableVisualFieldWarningsRequest
                    : handleEnableVisualFieldWarningsRequest
                }
                disabled={!isOnline}
                sx={{
                  opacity: !isOnline ? 0.3 : 1,
                }}
              >
                {isWarningsEnabled ? (
                  <HeadphonesIcon color="#000" alt="headset-warnings" />
                ) : (
                  <HeadphonesOffIcon color="#000" alt="headset-warnings" />
                )}
              </IconButton>
            </span>
          </Tooltip>
        )}
        <Tooltip
          title={
            isSubtitlesEnabled
              ? t("word_disable_subtitles")
              : t("word_enable_subtitles")
          }
        >
          <span>
            <IconButton
              aria-label="subtitles"
              onClick={
                isSubtitlesEnabled
                  ? handleDisableSubtitlesRequest
                  : handleEnableSubtitlesRequest
              }
              disabled={!isOnline}
              sx={{
                opacity: !isOnline ? 0.3 : 1,
              }}
            >
              {isSubtitlesEnabled ? (
                <SpeechBubbleIcon color="#000" alt="subtitles" />
              ) : (
                <SpeechBubbleOffIcon color="#000" alt="subtitles" />
              )}
            </IconButton>
          </span>
        </Tooltip>
      </Box>
      <Box
        display="flex"
        alignItems="center"
        justifyContent={{ xs: "flex-start", sm: "flex-end" }}
        minWidth="180px"
      >
        <Box height="24px" mr="14px">
          <VolumeIcon color="#000" />
        </Box>
        <Slider
          data-testid="volume-slider"
          min={0}
          max={100}
          value={volume}
          onChange={handleVolumeChange}
          onChangeCommitted={handleVolumeSet}
          sx={{
            height: "12px",
            "& .MuiSlider-thumb": {
              backgroundColor: "#E2772E",
              boxShadow: "none",
              border: "none",
            },
            "& .MuiSlider-track": {
              backgroundColor: "#E2772E",
              border: "none",
            },
            "& .MuiSlider-rail": {
              opacity: 1,
              backgroundColor: "#5B6D7A20",
              boxShadow: "none",
            },
          }}
        />
        <Typography
          data-testid="volume-label"
          style={{ width: "30px", marginLeft: "20px" }}
        >
          {volume}
        </Typography>
      </Box>
    </Box>
  );
};

export default LiveUpdateActions;
